<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { Icon } from '@iconify/vue'
import type { FormRules } from 'element-plus'
import type { IPreselectContainerSetupForm } from '@/types/core/forms'

interface ISelectOption {
  key: string
  value: string
}

interface IContainersSetup {
  type: string
  amount: number | null
}

const props = defineProps<{ selectOptions: ISelectOption[] }>()
const emit = defineEmits<{
  containerSelectionFormSubmit: [containerSetup: IContainersSetup]
  containerSelectionFormCancel: []
}>()

const formRef = ref()
const amountInputInvalid = ref(false)
const containerTypeInputInvalid = ref(false)
const errorClassName = computed(() => {
  return amountInputInvalid.value || containerTypeInputInvalid.value ? 'container-selection-form--error' : ''
})

const formModel = reactive<{
  selectedOption: string
  amount: number | null
}>({
  selectedOption: '',
  amount: null,
})

const formRules = reactive<FormRules<IPreselectContainerSetupForm>>({
  selectedOption: [
    {
      type: 'string',
      required: true,
      message: 'Please select container type.',
      trigger: ['blur', 'change', 'keydown'],
      asyncValidator: async (rule, value) => {
        if (!value) {
          containerTypeInputInvalid.value = true
          return Promise.reject('Please select container type.')
        }

        containerTypeInputInvalid.value = false
        return Promise.resolve()
      },
    },
  ],
  amount: [
    {
      required: true,
      trigger: ['blur', 'change', 'keydown'],
      asyncValidator: async (rule, value) => {
        if (!value) {
          amountInputInvalid.value = true
          return Promise.reject('Please specify container amount.')
        }

        if (value < 1) {
          amountInputInvalid.value = true
          return Promise.reject('Amount should be greater than 0.')
        }

        if (value >= 20) {
          amountInputInvalid.value = true
          return Promise.reject('Amount should be less than 20.')
        }

        amountInputInvalid.value = false
        return Promise.resolve()
      },
    },
  ],
})

const handleApply = async () => {
  await formRef.value?.validate((valid: boolean) => {
    if (!valid) {
      amountInputInvalid.value = true
      containerTypeInputInvalid.value = true
      return
    }

    amountInputInvalid.value = false
    containerTypeInputInvalid.value = false

    emit('containerSelectionFormSubmit', {
      type: formModel.selectedOption,
      amount: formModel.amount,
    })
  })
}

const handleCancel = () => {
  emit('containerSelectionFormCancel')
}
</script>

<template>
  <el-form
    ref="formRef"
    :model="formModel"
    :rules="formRules"
    class="container-selection-form"
    :class="errorClassName"
    :scroll-into-view-options="{ behavior: 'smooth', block: 'start' }"
    scroll-to-error
  >
    <el-form-item class="container-selection-form__input container-selection-form__input-select" prop="selectedOption">
      <el-select
        v-model="formModel.selectedOption"
        class="container-selection-form__input container-selection-form__input-select"
        placeholder="Select container type"
      >
        <el-option v-for="option in props.selectOptions" :key="option.key" :value="option.value">
          {{ option.value }}
        </el-option>
      </el-select>
    </el-form-item>
    <el-form-item class="container-selection-form__input container-selection-form__input-amount" prop="amount">
      <el-input
        v-model="formModel.amount"
        class="container-selection-form__input container-selection-form__input-amount"
        placeholder="Amount"
        min="1"
        max="20"
        type="number"
      />
    </el-form-item>
    <el-button type="primary" @click="handleApply">Apply</el-button>
    <div class="container-selection-form__action">
      <Icon icon="material-symbols:close" height="20" width="20" :inline="true" @click="handleCancel" />
    </div>
  </el-form>
</template>

<style scoped lang="scss">
.container-selection-form {
  display: flex;
  flex-direction: row;
  background: var(--fill-tertiary);
  border-radius: var(--spacing-spacing-xs);
  padding: var(--spacing-spacing-xs);
  gap: var(--spacing-spacing-xs);

  &--error {
    padding-bottom: var(--spacing-spacing-lg);
  }

  &__action {
    display: flex;
    align-items: center;
    cursor: pointer;
    color: var(--text-component-label);
  }

  &__input {
    background: var(--component-fill-primary);
    border-radius: var(--spacing-spacing-xs);
    margin-bottom: 0;

    &-select {
      flex: 1;
    }

    &-amount {
      flex: 1;
    }
  }
}
</style>
